import { useState, useCallback } from 'react';

export interface ValidationError {
    field: string;
    message: string;
}

export interface FieldValidationResult {
    isValid: boolean;
    error?: string;
}

/**
 * Hook for field validation
 */
export const useFieldValidation = () => {
    const [errors, setErrors] = useState<Record<string, string>>({});

    /**
     * Validate API Token
     * Must start with "EAA" and have at least 20 characters
     */
    const validateApiToken = useCallback((token: string): FieldValidationResult => {
        if (!token || token.trim().length === 0) {
            return { isValid: false, error: 'Token é obrigatório' };
        }
        if (token.length < 20) {
            return { isValid: false, error: 'Token deve ter no mínimo 20 caracteres' };
        }
        if (!token.startsWith('EAA')) {
            return { isValid: false, error: 'Token do Meta deve começar com "EAA"' };
        }
        return { isValid: true };
    }, []);

    /**
     * Validate Phone Number ID
     * Must be numeric only and at least 10 digits
     */
    const validatePhoneNumberId = useCallback((phoneId: string): FieldValidationResult => {
        if (!phoneId || phoneId.trim().length === 0) {
            return { isValid: false, error: 'Phone Number ID é obrigatório' };
        }
        if (!/^\d+$/.test(phoneId)) {
            return { isValid: false, error: 'Apenas números são permitidos' };
        }
        if (phoneId.length < 10) {
            return { isValid: false, error: 'Deve ter no mínimo 10 dígitos' };
        }
        return { isValid: true };
    }, []);

    /**
     * Validate Verification Token
     * Minimum 10 characters, recommended 32 for security
     */
    const validateVerificationToken = useCallback((token: string): FieldValidationResult => {
        if (!token || token.trim().length === 0) {
            return { isValid: false, error: 'Token de verificação é obrigatório' };
        }
        if (token.length < 10) {
            return { isValid: false, error: 'Token deve ter no mínimo 10 caracteres' };
        }
        if (token.length < 32) {
            return { 
                isValid: true, 
                error: 'Recomendado pelo menos 32 caracteres para maior segurança' 
            };
        }
        return { isValid: true };
    }, []);

    /**
     * Set error for a specific field
     */
    const setFieldError = useCallback((field: string, error: string) => {
        setErrors(prev => ({ ...prev, [field]: error }));
    }, []);

    /**
     * Clear error for a specific field
     */
    const clearFieldError = useCallback((field: string) => {
        setErrors(prev => {
            const newErrors = { ...prev };
            delete newErrors[field];
            return newErrors;
        });
    }, []);

    /**
     * Clear all errors
     */
    const clearAllErrors = useCallback(() => {
        setErrors({});
    }, []);

    /**
     * Validate a field and update errors state
     */
    const validateField = useCallback((field: string, value: string): boolean => {
        let result: FieldValidationResult;

        switch (field) {
            case 'api_token':
                result = validateApiToken(value);
                break;
            case 'phone_number_id':
                result = validatePhoneNumberId(value);
                break;
            case 'verification_token':
                result = validateVerificationToken(value);
                break;
            default:
                return true;
        }

        if (result.error) {
            setFieldError(field, result.error);
        } else {
            clearFieldError(field);
        }

        return result.isValid;
    }, [validateApiToken, validatePhoneNumberId, validateVerificationToken, setFieldError, clearFieldError]);

    return {
        errors,
        validateApiToken,
        validatePhoneNumberId,
        validateVerificationToken,
        validateField,
        setFieldError,
        clearFieldError,
        clearAllErrors
    };
};
